import React, { useState, useEffect, useCallback } from 'react';
import { exportToPdf } from '../utils/exportToPdf';
import { syncDateRange } from '../utils/dateUtils';

// Front Office → Invoice Amendment Audit (Phase 4 of SRS_INVOICE_AMENDMENT.md)
// Property-wide audit of every NARRATIVE_EDIT and CANCEL_AND_REISSUE event.
// Gated by can_invoice_amend (admin/hotel_admin auto-grants).
export default function PmsInvoiceAmendmentAudit({ token, selectedProperty }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = (() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); })();

  const [filters, setFilters] = useState({
    from_date: monthAgo,
    to_date: today,
    action: '',
    invoice_no: ''
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  }), [token, selectedProperty]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.append(k, v); });
      const res = await fetch(`/api/v1/pms/amendment-audit?${q.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load amendment audit');
      setRows(data.rows || []);
    } catch (e) {
      setError(e.message || 'Load failed');
    } finally { setLoading(false); }
  }, [filters, headers]);

  useEffect(() => { load(); /* initial */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // syncDateRange keeps the pair consistent — picking a From after the To (or a
  // To before the From) drags the other end along instead of leaving an
  // inverted range that silently returns no rows.
  const setF = (k, v) => setFilters((f) => syncDateRange({ ...f, [k]: v }, 'from_date', 'to_date', k));

  const diffSummary = (row) => {
    // Best-effort one-line summary of the change for the table.
    if (row.action === 'CANCEL_AND_REISSUE') {
      return `Cancelled → CN ${row.credit_note_no || '—'}`;
    }
    if (row.action === 'ADD_CHARGES_REISSUE' || row.action === 'ADD_CHARGES_SUPPLEMENTARY') {
      const a = row.after_json || {};
      const items = (a.charges || []).map((c) => `${c.description} ₹${Number(c.amount || 0).toFixed(2)}`).join(', ');
      return row.action === 'ADD_CHARGES_REISSUE'
        ? `Added ${items} → replaced by ${a.new_invoice_no || '—'}`
        : `Added ${items} → supplementary ${a.new_invoice_no || '—'}`;
    }
    // NARRATIVE_EDIT — list which fields changed.
    const before = row.before_json || {};
    const after  = row.after_json  || {};
    const fields = Object.keys(after);
    if (fields.length === 0) return '(no diff)';
    return fields.map((f) => `${f}: "${before[f] ?? '∅'}" → "${after[f] ?? '∅'}"`).join('; ');
  };

  const exportCsv = () => {
    if (rows.length === 0) return;
    const out = [
      ['Timestamp', 'Action', 'Invoice', 'Guest', 'Room', 'Reason', 'Changes', 'CN no.', 'Performed by'],
      ...rows.map((r) => [
        r.performed_at,
        r.action,
        r.invoice_no,
        r.guest_name || '',
        r.room_number || '',
        r.reason || '',
        diffSummary(r),
        r.credit_note_no || '',
        r.performed_by_name || `#${r.performed_by}`
      ])
    ];
    const csv = out.map((row) =>
      row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-amendment-audit_${filters.from_date}_to_${filters.to_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdfReport = () => {
    if (rows.length === 0) return;
    exportToPdf({
      title: 'Invoice Amendment Audit',
      subtitle: `${selectedProperty?.name || ''}  ·  ${filters.from_date} → ${filters.to_date}`,
      meta: [`Generated: ${new Date().toLocaleString()}`, `${rows.length} amendments`],
      orientation: 'landscape',
      filename: `invoice-amendment-audit_${filters.from_date}_to_${filters.to_date}.pdf`,
      compact: true,
      print: true,
      sections: [{
        head: [['Time', 'Action', 'Invoice', 'Guest', 'Room', 'Reason', 'Changes', 'CN no.', 'By']],
        body: rows.map((r) => [
          new Date(r.performed_at).toLocaleString(),
          r.action,
          r.invoice_no,
          r.guest_name || '—',
          r.room_number || '—',
          (r.reason || '').slice(0, 60),
          diffSummary(r).slice(0, 80),
          r.credit_note_no || '',
          r.performed_by_name || `#${r.performed_by}`
        ])
      }]
    });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b' }}>Invoice Amendment Audit</h2>
          <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: 4 }}>
            Every NARRATIVE_EDIT and CANCEL_AND_REISSUE event with full before/after snapshots
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={exportCsv} disabled={rows.length === 0} style={btnSecondary}>Export CSV</button>
          <button onClick={exportPdfReport} disabled={rows.length === 0} style={btnSecondary}>Export PDF</button>
          <button onClick={load} disabled={loading} style={btnPrimary}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
        padding: 14, marginBottom: 12, display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10
      }}>
        <div>
          <label style={lbl}>From date</label>
          <input type="date" style={inp} value={filters.from_date} onChange={(e) => setF('from_date', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>To date</label>
          <input type="date" style={inp} value={filters.to_date} onChange={(e) => setF('to_date', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Action</label>
          <select style={inp} value={filters.action} onChange={(e) => setF('action', e.target.value)}>
            <option value="">All</option>
            <option value="NARRATIVE_EDIT">Narrative edit</option>
            <option value="CANCEL_AND_REISSUE">Cancel &amp; Reissue</option>
            <option value="ADD_CHARGES_REISSUE">Add charges — replaced invoice</option>
            <option value="ADD_CHARGES_SUPPLEMENTARY">Add charges — supplementary invoice</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Invoice no.</label>
          <input style={inp} value={filters.invoice_no} onChange={(e) => setF('invoice_no', e.target.value)} placeholder="INV-…" />
        </div>
        <div style={{ display: 'flex', alignItems: 'end' }}>
          <button onClick={load} disabled={loading} style={{ ...btnPrimary, width: '100%' }}>Apply filters</button>
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      {rows.length === 0 && !loading ? (
        <div style={{
          background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8,
          padding: 40, textAlign: 'center', color: '#475569'
        }}>
          No amendments in this date range.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
              <tr>
                <th style={th}>Time</th>
                <th style={th}>Action</th>
                <th style={th}>Invoice</th>
                <th style={th}>Guest</th>
                <th style={th}>Room</th>
                <th style={th}>Reason</th>
                <th style={th}>Changes</th>
                <th style={th}>CN no.</th>
                <th style={th}>By</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.log_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={td}>{new Date(r.performed_at).toLocaleString()}</td>
                  <td style={td}>
                    <span style={actionPill(r.action)}>{r.action.replace(/_/g, ' ')}</span>
                  </td>
                  <td style={{ ...td, fontWeight: 600 }}>{r.invoice_no}</td>
                  <td style={td}>{r.guest_name || '—'}</td>
                  <td style={td}>{r.room_number || '—'}</td>
                  <td style={{ ...td, color: '#64748b' }}>{r.reason || ''}</td>
                  <td style={{ ...td, fontSize: '0.8rem', maxWidth: 380, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={diffSummary(r)}>
                    {diffSummary(r)}
                  </td>
                  <td style={td}>{r.credit_note_no || ''}</td>
                  <td style={td}>{r.performed_by_name || `#${r.performed_by}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function actionPill(action) {
  const c = action === 'CANCEL_AND_REISSUE'
    ? { bg: '#fee2e2', fg: '#991b1b' }
    : String(action || '').startsWith('ADD_CHARGES')
      ? { bg: '#fef3c7', fg: '#92400e' }
      : { bg: '#dbeafe', fg: '#1e40af' };
  return {
    background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 999,
    fontSize: '0.72rem', fontWeight: 600
  };
}

const th = { padding: '8px 10px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '8px 10px', color: '#0f172a' };
const lbl = { display: 'block', color: '#475569', fontSize: '0.75rem', fontWeight: 500, marginBottom: 4 };
const inp = { padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const btnPrimary   = { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' };
